import React, { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import { Grid, makeStyles, Switch, Theme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { EventType, RouteStopFragment } from "../../../api/nachfahrbarkeit-api/generated";
import { Text } from "../../../components/text/text";
import { RouteDetailsMapInfoWindowInfo } from "./route-details-map-info-window-info";
import { RouteDetailsMapInfoWindowButtons } from "./route-details-map-info-window-buttons";
import { EditRouteStopsContext } from "./edit-route-stops.context";
import { RouteContext } from "./route-context";

const useStyles = makeStyles((theme: Theme) => ({
  infoWindow: {
    minHeight: 10,
    width: 350,
  },
}));

interface IRouteDetailsMapInfoWindowProps {
  stop: RouteStopFragment;
}

const propNameSpacing = 5;
const propValueSpacing = 7;

export const RouteDetailsMapInfoWindow: FunctionComponent<IRouteDetailsMapInfoWindowProps> = (props) => {
  const { stop } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { isInEditMode, updateStop } = useContext(EditRouteStopsContext);
  const { setSelectedStopId } = useContext(RouteContext);
  const eventWithInfo = useMemo(
    () => stop.events?.find((event) => event && event.event_type === EventType.bulk && event.info),
    [stop],
  );

  const [info, setInfo] = useState<string | undefined>(eventWithInfo?.info || undefined);
  const [backwards, setBackwards] = useState<boolean>(
    !!stop.events?.find((event) => event.event_type === EventType.backwards) || false,
  );

  const handleStopUpdate = useCallback(() => {
    updateStop(stop.id, info, backwards);
    setSelectedStopId(null);
  }, [stop, info, backwards, updateStop, setSelectedStopId]);

  return (
    <Grid container className={classes.infoWindow} alignItems="center">
      <Grid item xs={propNameSpacing}>
        <Text bold>{t("routes_management.info_window.lat").toUpperCase()}</Text>
      </Grid>
      <Grid item xs={propValueSpacing}>
        <Text bold={false}>{stop.latitude}</Text>
      </Grid>
      <Grid item xs={propNameSpacing}>
        <Text bold>{t("routes_management.info_window.long").toUpperCase()}</Text>
      </Grid>
      <Grid item xs={propValueSpacing}>
        <Text bold={false}>{stop.longitude}</Text>
      </Grid>
      <Grid item xs={propNameSpacing}>
        <Text bold>{t("routes_management.info_window.info").toUpperCase()}</Text>
      </Grid>
      <Grid container item xs={propValueSpacing} direction="row" alignItems="center">
        <RouteDetailsMapInfoWindowInfo info={info} images={eventWithInfo?.images || []} onInfoChange={setInfo} />
      </Grid>
      <Grid item xs={propNameSpacing}>
        <Text bold>{t("routes_management.info_window.backwards").toUpperCase()}</Text>
      </Grid>
      <Grid item xs={propValueSpacing}>
        <Switch
          color="primary"
          disabled={!isInEditMode}
          checked={backwards}
          onChange={() => setBackwards(!backwards)}
        />
      </Grid>
      <RouteDetailsMapInfoWindowButtons stop={stop} onSave={handleStopUpdate} />
    </Grid>
  );
};
